import React, { useEffect, useState, useRef } from 'react'
import { TextField } from '@mui/material'

interface SmartDatePickerProps {
    value?: Date | string | null
    onChange: (value: Date | null) => void
    error?: boolean
    helperText?: string
    /**
     * For set in read only in EditorForm
     */
    disabled?: boolean
}

function dateToInputValue(date: Date | string | null | undefined): string {
    if (!date) {
        return ''
    }

    let dateObj: Date

    if (typeof date === 'string') {
        dateObj = new Date(date)
    } else if (date instanceof Date) {
        dateObj = date
    } else {
        return ''
    }

    if (isNaN(dateObj.getTime())) {
        return ''
    }

    const year = dateObj.getFullYear()
    const month = String(dateObj.getMonth() + 1).padStart(2, '0')
    const day = String(dateObj.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

function inputValueToDate(value: string): Date | null {
    if (!value) return null
    const [year, month, day] = value.split('-').map(Number)
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
        return null
    }
    return new Date(year, month - 1, day, 12, 0, 0)
}

const SmartDatePicker = ({ value, onChange, error, helperText, disabled }: SmartDatePickerProps) => {
    const [dateValue, setDateValue] = useState<string>(dateToInputValue(value) || '')
    const inputRef = useRef<HTMLInputElement>(null)
    const [isTyping, setIsTyping] = useState(false)

    useEffect(() => {
        if (!isTyping) {
            setDateValue(dateToInputValue(value) || '')
        }
    }, [value, isTyping])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value
        setDateValue(newValue)

        if (newValue.length === 10) {
            const date = inputValueToDate(newValue)
            if (date && !isNaN(date.getTime())) {
                onChange(date)
            }
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault()
        }
    }

    const handleFocus = () => {
        setIsTyping(true)
    }

    const handleBlur = () => {
        setIsTyping(false)
        if (dateValue.length === 10) {
            const date = inputValueToDate(dateValue)
            if (date && !isNaN(date.getTime())) {
                onChange(date)
            } else {
                setDateValue(dateToInputValue(value) || '')
            }
        } else if (dateValue.length === 0) {
            onChange(null)
        } else {
            setDateValue(dateToInputValue(value) || '')
        }
    }

    return (
        <TextField
            disabled={disabled}
            type='date'
            error={error}
            helperText={helperText}
            value={dateValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            inputRef={inputRef}
            fullWidth
            InputLabelProps={{
                shrink: true
            }}
            inputProps={{
                style: {
                    WebkitAppearance: 'none',
                    MozAppearance: 'textfield'
                }
            }}
        />
    )
}

export default SmartDatePicker
